// Extrato de Tentos (canvas T04): saldo, disponível e reservado; reservas e movimentos por dia; desistir da compra.
import { useState } from 'react';
import { type ItemExtrato, api, diaMes, lerTentos, reais } from '../comum/api.ts';
import type { Sessao } from '../comum/conta.ts';
import { medir } from '../comum/medicao.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { codigoDaReserva, comSinal, descrever, porDia, rotuloDoDia } from '../comum/tentos.ts';
import { IconeDoMovimento, Tento, usePainelTentos } from './tentos-comum.tsx';
import { itemDoPacote } from './LojaTentos.tsx';
import { Carregando, Tela, Topo } from './comum.tsx';

type Linha =
  | { tipo: 'reserva'; sala: string; quantidade: number; em: number }
  | { tipo: 'movimento'; item: ItemExtrato; em: number };

export function ExtratoTentos({ sessao }: { sessao: Sessao }) {
  const { painel, setPainel, erro: erroPainel, carregar } = usePainelTentos(sessao);
  const [confirmar, setConfirmar] = useState<string | null>(null); // sessão da compra
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  if (!painel) {
    return erroPainel ? (
      <Tela>
        <Topo titulo="Extrato de Tentos" voltar={voltar} />
        <p className="erro" role="alert">{erroPainel}</p>
        <button className="btn2 largo" onClick={() => void carregar()}>Tentar de novo</button>
      </Tela>
    ) : <Carregando />;
  }

  const agora = Date.now();
  const linhas: Linha[] = [
    ...painel.reservas.map((r): Linha => ({ tipo: 'reserva', sala: r.sala, quantidade: r.quantidade, em: r.criadaEm })),
    ...painel.extrato.map((m): Linha => ({ tipo: 'movimento', item: m, em: m.em })),
  ].sort((a, b) => b.em - a.em);

  async function desistir(sessaoCompra: string, item: ItemExtrato) {
    setErro(null); setOk(null); setOcupado(true);
    try {
      await api(sessao, 'POST', '/tentos/desistir', { sessao: sessaoCompra });
      const centavos = typeof item.detalhes.centavos === 'number' ? item.detalhes.centavos : 0;
      medir('refund', { currency: 'BRL', value: centavos / 100, transaction_id: sessaoCompra, items: [itemDoPacote({ chave: item.detalhes.pacote as never, tentos: item.quantidade, centavos, desconto: 0, liberado: true })] });
      setConfirmar(null);
      setOk(`Pronto. ${reais(centavos)} voltam em alguns dias e os ${item.quantidade} Tentos saíram do saldo.`);
      await carregar();
    } catch (e) { setErro((e as Error).message); } finally { setOcupado(false); }
  }

  async function verMais() {
    const ultimo = painel!.extrato.at(-1);
    if (!ultimo) return;
    try {
      const p = await lerTentos(sessao, ultimo.id);
      setPainel({ ...painel!, extrato: [...painel!.extrato, ...p.extrato], mais: p.mais });
    } catch (e) { setErro((e as Error).message); }
  }

  return (
    <Tela>
      <Topo titulo="Extrato de Tentos" voltar={voltar} />
      <section className="tres-saldos" aria-label="Seus Tentos">
        <div><span>SALDO</span><b className="dourado">{painel.saldo}</b></div>
        <div><span>DISPONÍVEL</span><b>{painel.disponivel}</b></div>
        <div><span>RESERVADO</span><b className="apagado">{painel.reservado}</b></div>
      </section>
      {ok && <p className="ok" role="status">{ok}</p>}
      {erro && <p className="erro" role="alert">{erro}</p>}

      {linhas.length === 0 && <p className="nota">Nenhum movimento ainda. Os Tentos do plano, das compras e das partidas aparecem aqui.</p>}
      {porDia(linhas, (l) => l.em).map((g) => (
        <section key={g.dia} aria-label={rotuloDoDia(g.itens[0]!.em, agora)}>
          <h2 className="rotulo">{rotuloDoDia(g.itens[0]!.em, agora)}</h2>
          <ul className="cartao extrato">
            {g.itens.map((l) => {
              if (l.tipo === 'reserva') {
                return (
                  <li key={'r' + l.sala}>
                    <div className="mov">
                      <IconeDoMovimento icone="reservado" />
                      <p><b>Ingresso reservado</b><span>Sala {codigoDaReserva(l.sala)} · esperando 6</span></p>
                      <b className="qtd-mov neutro">{l.quantidade}</b>
                    </div>
                  </li>
                );
              }
              const m = l.item, d = descrever(m);
              const ate = m.tipo === 'compra' && m.sessao ? painel.loja.desistiveis[m.sessao] : undefined;
              const sessaoCompra = ate ? m.sessao : undefined;
              return (
                <li key={m.id}>
                  <div className="mov">
                    <IconeDoMovimento icone={d.icone} plano={d.plano} />
                    <p><b>{d.titulo}</b><span>{d.detalhe}</span></p>
                    <b className={'qtd-mov ' + d.cor}>{comSinal(m.quantidade)}</b>
                  </div>
                  {sessaoCompra && ate && confirmar !== sessaoCompra && (
                    <button className="btn2 largo" disabled={ocupado} onClick={() => { setConfirmar(sessaoCompra); setOk(null); setErro(null); }}>Desistir da compra · até {diaMes(ate)}</button>
                  )}
                  {sessaoCompra && confirmar === sessaoCompra && (
                    <div className="confirmar-desistir">
                      <p>Devolvemos {typeof m.detalhes.centavos === 'number' ? reais(m.detalhes.centavos) : 'o valor'} e tiramos {m.quantidade} Tentos do saldo.</p>
                      <div className="dupla">
                        <button className="btn2" disabled={ocupado} onClick={() => setConfirmar(null)}>Manter</button>
                        <button className="btn2 perigo" disabled={ocupado} onClick={() => void desistir(sessaoCompra, m)}>Desistir</button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {painel.mais && <button className="link" onClick={() => void verMais()}>Ver mais</button>}

      <p className="nota">O reservado volta para o saldo se a sala desfizer antes de começar.</p>
      <button className="btn2 largo comprar-tentos" onClick={() => ir({ tela: 'loja' })}><Tento tamanho={18} vinco={false} />Comprar Tentos</button>
    </Tela>
  );
}
